import { FormEvent, useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import Button from '../common/Button';
import Input from '../common/Input';
import { SectionCard, Spinner } from '../common/PageHeader';
import { TextArea } from '../common/FormField';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { queryKeys } from '../../lib/queryClient';
import { api, errorMessage } from '../../services/api';
import { CompanyProfile } from '../../types';
import { blankToNull } from '../../utils/format';
import { CODE_PATTERN, compact, focusFirstError, GSTIN_PATTERN, hasErrors, MAX_PAYMENT_TERMS_DAYS, serverFieldErrors, v } from '../../utils/validation';

// Keep in sync with the backend DTO cap (~750 KB image once base64-decoded).
const MAX_LOGO_BYTES = 750 * 1024;
const ACCEPTED_LOGO_TYPES = ['image/png', 'image/jpeg'];

interface FormState {
  companyName: string;
  companyTagline: string;
  companyAddress: string;
  gstNumber: string;
  panNumber: string;
  cinNumber: string;
  contactNumber: string;
  email: string;
  website: string;
  jurisdiction: string;
  bankAccountName: string;
  bankName: string;
  bankAccountNo: string;
  bankIfsc: string;
  bankBranch: string;
  upiId: string;
  invoicePrefix: string;
  defaultGstPercent: string;
  defaultHsnSac: string;
  defaultPaymentTermsDays: string;
  invoiceTerms: string;
}

const EMPTY: FormState = {
  companyName: '', companyTagline: '', companyAddress: '', gstNumber: '', panNumber: '', cinNumber: '',
  contactNumber: '', email: '', website: '', jurisdiction: '', bankAccountName: '', bankName: '',
  bankAccountNo: '', bankIfsc: '', bankBranch: '', upiId: '', invoicePrefix: 'ASG', defaultGstPercent: '18',
  defaultHsnSac: '998314', defaultPaymentTermsDays: '15', invoiceTerms: '',
};

export default function CompanyProfileTab() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const { hasPermission } = useAuth();
  const canManage = hasPermission('company:manage');

  const [form, setForm] = useState<FormState>(EMPTY);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const [logo, setLogo] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);

  const { data: profile, isLoading } = useQuery({
    queryKey: queryKeys.company(),
    queryFn: () => api.get<CompanyProfile | null>('/company-profile'),
  });

  useEffect(() => {
    if (!profile) return;
    const next = { ...EMPTY };
    (Object.keys(EMPTY) as (keyof FormState)[]).forEach((k) => {
      const v = (profile as any)[k];
      next[k] = v === null || v === undefined ? '' : String(v);
    });
    setForm(next);
    setLogo(profile.logoBase64);
  }, [profile]);

  const saveMutation = useMutation({
    mutationFn: (payload: Record<string, unknown>) => api.put('/company-profile', payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.company() }),
  });

  function setField(key: keyof FormState, value: string) {
    setForm((p) => ({ ...p, [key]: value }));
    setErrors((p) => (p[key] ? { ...p, [key]: undefined } : p));
  }

  function onLogoPicked(file?: File) {
    if (!file) return;
    if (!ACCEPTED_LOGO_TYPES.includes(file.type)) return showToast('Logo must be a PNG or JPEG image', 'error');
    if (file.size > MAX_LOGO_BYTES) return showToast('Logo must be smaller than 750 KB', 'error');
    const reader = new FileReader();
    reader.onload = () => setLogo(String(reader.result));
    reader.readAsDataURL(file);
  }

  function validate(): boolean {
    // Bank details print together on the invoice — once any one of the three
    // key fields is filled, all three are required.
    const anyBank = !!(form.bankName.trim() || form.bankAccountNo || form.bankIfsc);
    const e = compact({
      companyName: v.businessName(form.companyName, 'Company name', true),
      companyTagline: v.text(form.companyTagline, 'Tagline', { max: 255 }),
      companyAddress: v.text(form.companyAddress, 'Registered address', { required: true, min: 10, max: 500 }),
      contactNumber: v.phone(form.contactNumber, false, 'Contact number'),
      email: v.email(form.email),
      website: v.website(form.website),
      jurisdiction: v.label(form.jurisdiction, 'Jurisdiction'),
      gstNumber: v.gstin(form.gstNumber),
      panNumber: v.pan(form.panNumber) ?? v.panMatchesGstin(form.panNumber, form.gstNumber),
      cinNumber: v.cin(form.cinNumber),
      bankAccountName: v.businessName(form.bankAccountName, 'Account name'),
      bankName: v.businessName(form.bankName, 'Bank name', anyBank),
      bankAccountNo: v.bankAccount(form.bankAccountNo, anyBank),
      bankIfsc: v.ifsc(form.bankIfsc, anyBank),
      bankBranch: v.label(form.bankBranch, 'Branch', false, 255),
      upiId: v.upi(form.upiId),
      invoicePrefix: v.text(form.invoicePrefix, 'Invoice prefix', {
        required: true, min: 2, max: 10, pattern: CODE_PATTERN, patternMessage: 'Invoice prefix can only contain letters, digits and -',
      }),
      defaultGstPercent: v.gstPercent(form.defaultGstPercent, 'Default GST %'),
      defaultHsnSac: v.hsn(form.defaultHsnSac),
      defaultPaymentTermsDays: v.wholeNumber(form.defaultPaymentTermsDays, 'Payment terms', { required: true, min: 0, max: MAX_PAYMENT_TERMS_DAYS }),
      invoiceTerms: v.text(form.invoiceTerms, 'Terms', { max: 1000 }),
    });
    setErrors(e);
    if (hasErrors(e)) {
      showToast('Please fix the highlighted fields', 'error');
      focusFirstError();
    }
    return !hasErrors(e);
  }

  // Typing a GSTIN fills the PAN it encodes when the PAN is still blank.
  function onGstinChange(raw: string) {
    const gstin = raw.toUpperCase().replace(/[^A-Z0-9]/g, '');
    setForm((p) => ({ ...p, gstNumber: gstin, panNumber: !p.panNumber && GSTIN_PATTERN.test(gstin) ? gstin.slice(2, 12) : p.panNumber }));
    setErrors((p) => ({ ...p, gstNumber: undefined, panNumber: undefined }));
  }

  async function handleSubmit(ev: FormEvent) {
    ev.preventDefault();
    if (!validate()) return;
    const { defaultGstPercent, defaultPaymentTermsDays, companyName, companyAddress, invoicePrefix, defaultHsnSac, ...optional } = form;
    try {
      await saveMutation.mutateAsync({
        ...blankToNull(optional),
        companyName: companyName.trim(),
        companyAddress: companyAddress.trim(),
        invoicePrefix: invoicePrefix.toUpperCase(),
        defaultHsnSac,
        defaultGstPercent: Number(defaultGstPercent),
        defaultPaymentTermsDays: Number(defaultPaymentTermsDays),
        logoBase64: logo,
      });
      showToast('Company profile saved', 'success');
    } catch (err) {
      setErrors(serverFieldErrors(err, Object.keys(EMPTY) as (keyof FormState)[]));
      showToast(errorMessage(err, 'Failed to save company profile'), 'error');
    }
  }

  if (isLoading) return <Spinner />;

  const ro = !canManage;
  const fy = (() => {
    const d = new Date();
    const s = d.getMonth() >= 3 ? d.getFullYear() : d.getFullYear() - 1;
    return `${s}-${String((s + 1) % 100).padStart(2, '0')}`;
  })();

  return (
    <form onSubmit={handleSubmit} className="space-y-4" noValidate>
      {ro && (
        <div className="rounded-lg bg-ink-100 px-4 py-2.5 text-sm text-ink-600">
          <i className="fas fa-lock mr-2" />Only an administrator can change the company profile.
        </div>
      )}

      <SectionCard title="Invoice Header">
        <div className="flex flex-col sm:flex-row gap-5">
          <div className="shrink-0">
            <p className="text-xs font-bold uppercase tracking-[0.06em] text-ink-600 mb-1.5">Logo</p>
            <div className="w-36 h-24 rounded-lg border border-dashed border-ink-300 bg-ink-50 flex items-center justify-center overflow-hidden">
              {logo ? <img src={logo} alt="Company logo" className="max-w-full max-h-full object-contain" /> : <i className="fas fa-image text-2xl text-ink-300" />}
            </div>
            {!ro && (
              <div className="mt-2 flex gap-3 text-xs font-semibold">
                <button type="button" className="text-brand-600 hover:underline" onClick={() => fileRef.current?.click()}>Upload</button>
                {logo && <button type="button" className="text-red-600 hover:underline" onClick={() => setLogo(null)}>Remove</button>}
              </div>
            )}
            <input ref={fileRef} type="file" accept="image/png,image/jpeg" className="hidden"
              onChange={(e) => { onLogoPicked(e.target.files?.[0]); e.target.value = ''; }} />
            <p className="text-[11px] text-ink-400 mt-1">PNG / JPEG, up to 750 KB</p>
          </div>
          <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input className="sm:col-span-2" label="Company Name" required disabled={ro} value={form.companyName} maxLength={255}
              onChange={(e) => setField('companyName', e.target.value)} error={errors.companyName} />
            <Input className="sm:col-span-2" label="Tagline" disabled={ro} value={form.companyTagline} maxLength={255}
              onChange={(e) => setField('companyTagline', e.target.value)} error={errors.companyTagline} />
            <Input className="sm:col-span-2" label="Registered Address" required disabled={ro} value={form.companyAddress} maxLength={500}
              onChange={(e) => setField('companyAddress', e.target.value)} error={errors.companyAddress} />
            <Input label="Contact Number" type="tel" disabled={ro} value={form.contactNumber} maxLength={14}
              onChange={(e) => setField('contactNumber', e.target.value.replace(/[^0-9+\s-]/g, ''))} error={errors.contactNumber} />
            <Input label="Email" type="email" disabled={ro} value={form.email} maxLength={255}
              onChange={(e) => setField('email', e.target.value.trim())} error={errors.email} />
            <Input label="Website" disabled={ro} value={form.website} maxLength={255}
              onChange={(e) => setField('website', e.target.value.trim())} error={errors.website} />
            <Input label="Jurisdiction" disabled={ro} value={form.jurisdiction} maxLength={100} placeholder="e.g. Mumbai"
              onChange={(e) => setField('jurisdiction', e.target.value)} error={errors.jurisdiction}
              helperText='Prints "SUBJECT TO … JURISDICTION"' />
          </div>
        </div>
      </SectionCard>

      <SectionCard title="Tax Registration">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Input label="GSTIN" disabled={ro} value={form.gstNumber} maxLength={15}
            onChange={(e) => onGstinChange(e.target.value)} error={errors.gstNumber}
            helperText="15 characters — its state code decides CGST+SGST vs IGST" />
          <Input label="PAN" disabled={ro} value={form.panNumber} maxLength={10}
            onChange={(e) => setField('panNumber', e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))} error={errors.panNumber}
            helperText="10 characters" />
          <Input label="CIN" disabled={ro} value={form.cinNumber} maxLength={21}
            onChange={(e) => setField('cinNumber', e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))} error={errors.cinNumber}
            helperText="21 characters" />
        </div>
      </SectionCard>

      <SectionCard title="Bank Details (printed on invoices)">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Input label="Account Name" disabled={ro} value={form.bankAccountName} maxLength={255}
            onChange={(e) => setField('bankAccountName', e.target.value)} error={errors.bankAccountName} />
          <Input label="Bank Name" disabled={ro} value={form.bankName} maxLength={255}
            onChange={(e) => setField('bankName', e.target.value)} error={errors.bankName} />
          <Input label="Account Number" disabled={ro} value={form.bankAccountNo} maxLength={18}
            onChange={(e) => setField('bankAccountNo', e.target.value.replace(/\D/g, ''))} error={errors.bankAccountNo}
            helperText="9-18 digits" />
          <Input label="IFSC" disabled={ro} value={form.bankIfsc} maxLength={11}
            onChange={(e) => setField('bankIfsc', e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))} error={errors.bankIfsc}
            helperText="11 characters, e.g. HDFC0001234" />
          <Input label="Branch" disabled={ro} value={form.bankBranch} maxLength={255}
            onChange={(e) => setField('bankBranch', e.target.value)} error={errors.bankBranch} />
          <Input label="UPI ID" disabled={ro} value={form.upiId} maxLength={100}
            onChange={(e) => setField('upiId', e.target.value.trim())} error={errors.upiId} />
        </div>
        <p className="text-xs text-ink-500 mt-3">Bank name, account number and IFSC are required before an invoice can be generated.</p>
      </SectionCard>

      <SectionCard title="Invoice Defaults">
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
          <Input label="Invoice Prefix" required disabled={ro} value={form.invoicePrefix} maxLength={10}
            onChange={(e) => setField('invoicePrefix', e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, ''))} error={errors.invoicePrefix}
            helperText={`Next: ${form.invoicePrefix || 'ASG'}/${fy}/0001`} />
          <Input label="Default GST %" required type="number" disabled={ro} value={form.defaultGstPercent}
            onChange={(e) => setField('defaultGstPercent', e.target.value)} error={errors.defaultGstPercent} />
          <Input label="Default HSN / SAC" required disabled={ro} value={form.defaultHsnSac} maxLength={8}
            onChange={(e) => setField('defaultHsnSac', e.target.value.replace(/\D/g, ''))} error={errors.defaultHsnSac} />
          <Input label="Payment Terms (days)" required type="number" decimals={0} disabled={ro} value={form.defaultPaymentTermsDays} maxLength={3}
            onChange={(e) => setField('defaultPaymentTermsDays', e.target.value)} error={errors.defaultPaymentTermsDays}
            helperText="0-365" />
        </div>
        <TextArea className="mt-4" label="Terms & Conditions (printed on invoices)" rows={3} maxLength={1000} disabled={ro}
          value={form.invoiceTerms} onChange={(val) => setField('invoiceTerms', val)} error={errors.invoiceTerms} />
      </SectionCard>

      {!ro && (
        <div className="flex items-center justify-between gap-3">
          <p className="text-xs text-ink-500">Changes apply to invoices generated from now on — issued invoices keep their original details.</p>
          <Button type="submit" variant="primary" icon="fa-floppy-disk" disabled={saveMutation.isPending}>
            {saveMutation.isPending ? 'Saving…' : 'Save Profile'}
          </Button>
        </div>
      )}
    </form>
  );
}
